import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { sendContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await sendContact(form);
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <section className="max-w-3xl mx-auto px-6 lg:px-8 pb-28">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs tracking-accent uppercase text-amber-500 mb-4 flex items-center gap-2"><Mail size={14} /> Get in touch</p>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tight">Contact</h1>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
            For media inquiries, events, book club questions, or just to say hello. Drop me a line. I read every message.
          </p>

          <form onSubmit={onSubmit} data-testid="contact-form" className="mt-12 space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs tracking-accent uppercase text-zinc-500 mb-2">Name</label>
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs tracking-accent uppercase text-zinc-500 mb-2">Email</label>
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs tracking-accent uppercase text-zinc-500 mb-2">Message</label>
              <textarea
                data-testid="contact-message-input"
                required
                rows={6}
                value={form.message}
                onChange={update("message")}
                className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors resize-none"
                placeholder="What's on your mind?"
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-7 py-4 hover:bg-amber-400 transition-colors disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send message"} <Send size={16} />
            </button>
          </form>
        </motion.div>
      </section>
    </div>
  );
}
